import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import axios from 'axios';

const AuditLogs = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchFilters, setSearchFilters] = useState({
    entityType: '',
    action: '',
    actorId: '',
    startDate: '',
    endDate: '',
    charterNumber: '',
    sectorId: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const entityFilterOptions = stats?.entityTypeStats?.map((item) => item._id) || [];
  const actionFilterOptions = stats?.actionStats?.map((item) => item._id) || [];

  const fetchAuditLogs = async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
        ...Object.fromEntries(
          Object.entries(searchFilters).filter(([_, value]) => value !== '')
        )
      });

      const response = await axios.get(`/api/audit/search?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAuditLogs(response.data.logs);
      setPagination((prev) => ({
        ...prev,
        ...(response.data.pagination || {}),
        page: response.data.pagination?.page ?? page
      }));
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    // Prevent multiple simultaneous calls
    if (statsLoading) {
      return;
    }

    try {
      setStatsLoading(true);
      setStatsError(null);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams(
        Object.fromEntries(
          Object.entries(searchFilters).filter(([key, value]) => 
            key.includes('Date') && value !== ''
          )
        )
      );

      const response = await axios.get(`/api/audit/stats?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch audit stats:', error);
      
      // Handle rate limiting (429) specifically
      if (error.response?.status === 429) {
        setStatsError('Too many requests. Please wait a moment before searching again.');
      } else {
        setStatsError('Failed to load statistics. Please try again later.');
      }
      
      // Don't clear existing stats on error, just show the error message
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditLogs();
    fetchStats();
  }, []);

  const handleSearch = () => {
    fetchAuditLogs(1);
    fetchStats();
  };

  const handlePageChange = (event, page) => {
    setPagination((prev) => ({ ...prev, page }));
    fetchAuditLogs(page);
  };

  const handleLogClick = (log) => {
    setSelectedLog(log);
    setDetailDialogOpen(true);
  };

  const getActionColor = (action) => {
    const colors = {
      'TRIP_CREATED': 'success',
      'SECTOR_CREATED': 'info',
      'SECTOR_ASSIGNED': 'warning',
      'CREW_RESPONSE': 'primary',
      'PILOT_UPSERTED': 'secondary',
      'USER_CREATED': 'success',
      'USER_UPDATED': 'warning',
      'USER_DELETED': 'error'
    };
    return colors[action] || 'default';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  const formatActor = (actor) => {
    if (!actor) return 'System';
    return `${actor.firstName} ${actor.lastName} (${actor.email})`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Audit Logs
      </Typography>

      {/* Statistics */}
      {(stats || statsError) && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Audit Statistics</Typography>
            {statsError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {statsError}
              </Alert>
            )}
            {stats && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Typography variant="h4" color="primary">
                  {stats.totalLogs}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Logs
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="h4" color="secondary">
                  {stats.entityTypeStats.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Entity Types
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="h4" color="success.main">
                  {stats.actionStats.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Action Types
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="h4" color="warning.main">
                  {stats.topActors.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Users
                </Typography>
              </Grid>
            </Grid>
            )}
          </CardContent>
        </Card>
      )}

      {/* Search Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Search Filters</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Entity Type</InputLabel>
                <Select
                  value={searchFilters.entityType}
                  onChange={(e) => setSearchFilters(prev => ({ ...prev, entityType: e.target.value }))}
                  label="Entity Type"
                >
                  <MenuItem value="">All</MenuItem>
                  {entityFilterOptions.map((entityType) => (
                    <MenuItem key={entityType} value={entityType}>
                      {entityType}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Action</InputLabel>
                <Select
                  value={searchFilters.action}
                  onChange={(e) => setSearchFilters(prev => ({ ...prev, action: e.target.value }))}
                  label="Action"
                >
                  <MenuItem value="">All</MenuItem>
                  {actionFilterOptions.map((action) => (
                    <MenuItem key={action} value={action}>
                      {action}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                size="small"
                label="Start Date"
                type="date"
                value={searchFilters.startDate}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, startDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                size="small"
                label="End Date"
                type="date"
                value={searchFilters.endDate}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, endDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                size="small"
                label="Charter Number"
                placeholder="e.g., CH-GEA-0008"
                value={searchFilters.charterNumber}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, charterNumber: e.target.value.toUpperCase() }))}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                size="small"
                label="Sector ID"
                placeholder="Sector MongoDB ID"
                value={searchFilters.sectorId}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, sectorId: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={searchFilters.sortBy}
                  onChange={(e) => setSearchFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                  label="Sort By"
                >
                  <MenuItem value="createdAt">Timestamp</MenuItem>
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="action">Action</MenuItem>
                  <MenuItem value="entityType">Entity</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Order</InputLabel>
                <Select
                  value={searchFilters.sortOrder}
                  onChange={(e) => setSearchFilters(prev => ({ ...prev, sortOrder: e.target.value }))}
                  label="Order"
                >
                  <MenuItem value="desc">Descending</MenuItem>
                  <MenuItem value="asc">Ascending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={1}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading}
              >
                Search
              </Button>
            </Grid>
            <Grid item xs={12} sm={1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearchFilters({
                    entityType: '',
                    action: '',
                    actorId: '',
                    startDate: '',
                    endDate: '',
                    charterNumber: '',
                    sectorId: '',
                    sortBy: 'createdAt',
                    sortOrder: 'desc'
                  });
                  fetchAuditLogs(1);
                  fetchStats();
                }}
                disabled={loading}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Audit Logs Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Audit Logs ({pagination.total} total)
          </Typography>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Entity</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>IP Address</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLogs.map((log) => (
                  <TableRow 
                    key={log._id} 
                    hover 
                    onClick={() => handleLogClick(log)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ScheduleIcon sx={{ mr: 1, fontSize: 16 }} />
                        {formatDate(log.createdAt)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={log.entityType} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={log.action} 
                        color={getActionColor(log.action)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ mr: 1, fontSize: 16 }} />
                        {formatActor(log.actorId)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BusinessIcon sx={{ mr: 1, fontSize: 16 }} />
                        {log.clientId?.name || 'Unknown'}
                      </Box>
                    </TableCell>
                    <TableCell>{log.ip || 'N/A'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Audit Log Details</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Entity Type
                  </Typography>
                  <Typography variant="body1">
                    {selectedLog.entityType}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Action
                  </Typography>
                  <Typography variant="body1">
                    {selectedLog.action}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Actor
                  </Typography>
                  <Typography variant="body1">
                    {formatActor(selectedLog.actorId)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Timestamp
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedLog.createdAt)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    IP Address
                  </Typography>
                  <Typography variant="body1">
                    {selectedLog.ip || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    User Agent
                  </Typography>
                  <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                    {selectedLog.userAgent || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Before/After Data */}
              {(selectedLog.before || selectedLog.after) && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Data Changes
                  </Typography>
                  
                  {selectedLog.before && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Before</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <pre style={{ 
                          backgroundColor: '#f5f5f5', 
                          padding: '10px', 
                          borderRadius: '4px',
                          overflow: 'auto',
                          fontSize: '12px'
                        }}>
                          {JSON.stringify(selectedLog.before, null, 2)}
                        </pre>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {selectedLog.after && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>After</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <pre style={{ 
                          backgroundColor: '#f5f5f5', 
                          padding: '10px', 
                          borderRadius: '4px',
                          overflow: 'auto',
                          fontSize: '12px'
                        }}>
                          {JSON.stringify(selectedLog.after, null, 2)}
                        </pre>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogs;
